import { ReactElement, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { 
  useNotificationsAppNotificationsCountUnreadAppNotificationsRetrieveQuery,
  useNotificationsAppNotificationsListQuery,
  useNotificationsAppNotificationsPartialUpdateMutation,
  useNotificationsAppNotificationsMarkAllSeenPartialUpdateMutation,
} from '../../store/cabinetApi/generated/notifications';
import { scheduling } from '../../store/cabinetApi/scheduling';
import { crm } from '../../store/cabinetApi/crm';
import { AppNotification } from '../../store/cabinetApi/generated/notifications';
import { PAGE_URL } from '../../constants';
import { MeetingRequest } from '../../store/cabinetApi/generated/scheduling';
import { getLocalTimeZone } from '../../utils/scheduleUtils';
import { NotificationsDrawer } from './NotificationsDrawer';

const NotificationsDrawerContainer = (): ReactElement => {
  const [loadingAction, setLoadingAction] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const open = location.state?.showNotifications === true;
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  useNotificationsAppNotificationsCountUnreadAppNotificationsRetrieveQuery(
    undefined,
    { pollingInterval: 10 * 60 * 1000 } // 10 minutes
  );

  // Fetch unread and read notifications (refetch every 10 minutes)
  const {
    data: unreadNotifications, 
    isLoading: unreadLoading, 
    error: unreadError
  } = useNotificationsAppNotificationsListQuery(
    {read: false},
    { pollingInterval: 10 * 60 * 1000 }
  );
  
  const {
    data: readNotifications, 
    isLoading: readLoading, 
    error: readError
  } = useNotificationsAppNotificationsListQuery(
    {read: true},
    { pollingInterval: 10 * 60 * 1000 }
  );
  
  const {
    data: userPrefs,
  } = scheduling.useAppSchedulingPreferencesListQuery();
  
  const [updateNotification] = useNotificationsAppNotificationsPartialUpdateMutation();
  const [createMeeting] = scheduling.useAppSchedulingMeetingsCreateMutation();
  const [fetchCadence] = crm.useLazyAppCrmContactCadenceRetrieveQuery();
  const [markAllSeen] = useNotificationsAppNotificationsMarkAllSeenPartialUpdateMutation();

  // Mark all notifications as seen when drawer opens
  useEffect(() => {
    if (open) {
      markAllSeen({}).unwrap();
    }
  }, [open, markAllSeen]);

  const handleClose = () => {
    navigate(location.pathname, { 
      state: { ...location.state, showNotifications: false },
      replace: true 
    });
  };

  const handleNotificationAction = async (notification: AppNotification, action: string) => {
    setLoadingAction(notification.id);
    try {
      let redirectUrl: string | undefined;
      // Handle different notification actions
      if (action === 'accept') {
        // Use notification type instead of meta.type
        if (notification.type === 'CADENCE_REMINDER') {
          // For cadence reminders, we need to lookup the cadence to get the leader
          if (notification.resource?.id) {
            try {
              // Use the lazy query to fetch cadence details
              const cadenceResult = await fetchCadence(notification.resource.id).unwrap();
              
              // Create a new meeting based on notification data
              const meetingData: MeetingRequest = {
                title: notification.meta?.meeting_title || 'Meeting from Notification',
                description: '',
                duration_minutes: 30, // Default duration
                leaders: cadenceResult.teammate 
                  ? [cadenceResult.teammate] 
                  : currentUser?.profile.user_leader 
                    ? [currentUser.profile.user_leader]
                    : [],
                is_poll: false,
                prevent_conflict: true,
                status: 1, // Pending status,
                calendar_tz: userPrefs?.user_prefs?.default_calendar_timezone || getLocalTimeZone()?.name || null,
              };

              const createdMeeting = await createMeeting(meetingData).unwrap();
              
              // Close drawer and navigate to the newly created meeting
              navigate(`/schedule/${createdMeeting.id}/`, { 
                state: { ...location.state, showNotifications: false },
                replace: true 
              });
            } catch (error) {
              console.error('Error fetching cadence:', error);
            }
          }
        } else if (notification.type === 'CONTACT_TASK_DUE') {
          // Use resource.id for contact navigation
          if (notification.meta?.contact_ids?.[0]) {
            navigate(`${PAGE_URL.CRM_PEOPLE}/${notification.meta?.contact_ids[0]}`, { 
              state: { ...location.state, showNotifications: false },
              replace: true 
            });
          }
        } else if (notification.type === 'MEETING_LINK' && notification.meta?.url) {
          redirectUrl = notification.meta.url;
        }
      }
      
      // Mark notification as read after action
      await updateNotification({
        id: notification.id,
        patchedAppNotification: {
          read: true,
          read_date: new Date().toISOString(),
        }
      }).unwrap();
      if (redirectUrl) {
        window.location.assign(redirectUrl);
      }
    } catch (error) {
      console.error('Failed to handle notification action:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const formatNotificationDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <NotificationsDrawer
      open={open}
      unreadNotifications={unreadNotifications}
      readNotifications={readNotifications}
      unreadLoading={unreadLoading}
      readLoading={readLoading}
      unreadError={unreadError}
      readError={readError}
      loadingAction={loadingAction}
      onClose={handleClose}
      onNotificationAction={handleNotificationAction}
      formatNotificationDate={formatNotificationDate}
    />
  );
};

export default NotificationsDrawerContainer;
